/* eslint-disable */
import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableContainer,
  TableRow, TableCell, Typography, Collapse, IconButton, Skeleton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TableHeader from './TableHeader';
import TableRowItem from './TableRowItem';
import TableEmptyState from './EmptyState';
import TablePagination from './Pagination';

/* ── Group header row ────────────────────────────────────────── */
const GroupHeaderRow = ({ label, count, colSpan, collapsed, onToggle }) => (
  <TableRow sx={{ backgroundColor: '#F1F5F9' }}>
    <TableCell
      colSpan={colSpan}
      sx={{ py: '10px', px: '16px', borderBottom: '1px solid #DEE5EE' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconButton size="small" onClick={onToggle} sx={{ p: '2px', color: '#718096' }}>
          {collapsed
            ? <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        </IconButton>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {label}
        </Typography>
        <Box
          sx={{
            ml: '4px', px: '7px', py: '1px',
            backgroundColor: '#007BC6', borderRadius: '10px',
          }}
        >
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>
            {count}
          </Typography>
        </Box>
      </Box>
    </TableCell>
  </TableRow>
);

/* ── CustomTable ─────────────────────────────────────────────── */
/**
 * CustomTable — the main reusable table component.
 *
 * Props:
 *  columns       {array}     — column definitions (see README below)
 *  data          {array}     — array of row objects
 *  loading       {boolean}   — show skeleton/disabled state
 *  emptyMessage  {string}    — empty state message
 *
 *  selectable    {boolean}   — enable row checkboxes
 *  selected      {array}     — array of selected row ids
 *  onSelect      {function}  — (row) => void
 *  onSelectAll   {function}  — (allIds) => void
 *  rowId         {string}    — field to use as row key / identity (default 'id')
 *
 *  sortField     {string}    — controlled sort field
 *  sortDir       {string}    — 'asc' | 'desc'
 *  onSort        {function}  — (fieldId) => void
 *
 *  groupBy       {string}    — field name to group rows by
 *  groupLabel    {function}  — (groupKey) => string  — formats the group header label
 *  collapsible   {boolean}   — whether groups can be collapsed
 *
 *  page          {number}    — current page (1-based)
 *  pageSize      {number}    — rows per page
 *  total         {number}    — total row count (for pagination)
 *  onPageChange  {function}  — (page) => void
 *
 *  onRowClick    {function}  — (row) => void
 *  sx            {object}    — extra sx on container
 *
 * --- Column definition ---
 * {
 *   id:       string       — field key in data row
 *   label:    string       — header label
 *   width:    number|string
 *   align:    'left'|'center'|'right'
 *   bold:     boolean      — bold cell text
 *   noWrap:   boolean      — white-space: nowrap
 *   sortable: boolean
 *   render:   (row, index) => ReactNode   — custom cell renderer
 *   headerSx: object       — extra sx on TH
 *   cellSx:   object       — extra sx on TD
 * }
 */
const CustomTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage,
  selectable = false,
  selected = [],
  onSelect,
  onSelectAll,
  rowId = 'id',
  sortField,
  sortDir = 'asc',
  onSort,
  groupBy,
  groupLabel,
  collapsible = true,
  page = 1,
  pageSize = 10,
  total,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  onRowClick,
  sx = {},
  stickyTop,
  stickyBg = '#fff',
}) => {
  /* ── collapsed groups state ── */
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── selection helpers ── */
  const allSelected = data.length > 0 && data.every((r) => selected.includes(r[rowId]));
  const someSelected = selected.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (onSelectAll) onSelectAll(allSelected ? [] : data.map((r) => r[rowId]));
  };

  /* ── grouping ── */
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = {};
    data.forEach((row) => {
      const key = row[groupBy] ?? 'Autres';
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    return map;
  }, [data, groupBy]);

  /* ── column count for colSpan ── */
  const colCount = columns.length + (selectable ? 1 : 0) + (groupBy ? 1 : 0);

  /* ── render rows (flat or grouped) ── */
  const renderRows = () => {
    if (data.length === 0) {
      return <TableEmptyState message={emptyMessage} colSpan={colCount} />;
    }

    if (!grouped) {
      return data.map((row, i) => (
        <TableRowItem
          key={row[rowId] ?? i}
          row={row}
          columns={columns}
          index={i}
          selectable={selectable}
          selected={selected.includes(row[rowId])}
          onSelect={onSelect}
          onClick={onRowClick}
        />
      ));
    }

    /* Grouped rendering */
    return Object.entries(grouped).map(([key, rows]) => {
      const isCollapsed = collapsedGroups[key];
      const label = groupLabel ? groupLabel(key) : key;

      return (
        <React.Fragment key={key}>
          <GroupHeaderRow
            label={label}
            count={rows.length}
            colSpan={colCount}
            collapsed={isCollapsed}
            onToggle={() => collapsible && toggleGroup(key)}
          />
          {!isCollapsed && rows.map((row, i) => (
            <TableRowItem
              key={row[rowId] ?? i}
              row={row}
              columns={columns}
              index={i}
              selectable={selectable}
              selected={selected.includes(row[rowId])}
              onSelect={onSelect}
              onClick={onRowClick}
              hasGrouping
            />
          ))}
        </React.Fragment>
      );
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #DEE5EE',
        borderRadius: '10px',
        overflow: stickyTop ? 'visible' : 'hidden',
        opacity: loading && data.length > 0 ? 0.6 : 1,
        pointerEvents: loading ? 'none' : 'auto',
        ...sx,
      }}
    >
      <TableContainer sx={stickyTop ? { overflow: 'visible' } : {}}>
        <Table
          stickyHeader={!!stickyTop}
          sx={stickyTop ? { '& .MuiTableCell-stickyHeader': { top: stickyTop, backgroundColor: stickyBg } } : {}}
        >
          <TableHeader
            columns={columns}
            selectable={selectable}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            sortField={sortField}
            sortDir={sortDir}
            onSort={onSort}
            hasGrouping={!!groupBy}
          />
          <TableBody>
            {loading && data.length === 0
              ? [...Array(pageSize)].map((_, idx) => (
                <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                  {selectable && <TableCell sx={{ py: '10px', px: '12px', borderBottom: 'none' }}><Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: '4px' }} /></TableCell>}
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={{ py: '10px', px: '16px', borderBottom: 'none' }}>
                      <Skeleton variant={col.id === 'action' ? 'circular' : 'text'} width={col.id === 'action' ? 28 : '70%'} height={col.id === 'action' ? 28 : 22} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
              : renderRows()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {onPageChange && (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total ?? data.length}
          onChange={onPageChange}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </Box>
  );
};

export default CustomTable;
